import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useRef
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { debounce } from "lodash-es";
import { Input as AntInput } from "antd";

import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import "antd/es/input/style/css";
import "./search.override.css";
import styles from "./search.css";

export {
  ColumnSearchInput,
  COLUMN_SEARCH_SIZES
} from "./column_search/index.jsx";
export const SEARCH_SIZES = {
  BASE: "base",
  MEDIUM: "medium",
  SMALL: "small",
  SMALL_DEPRECATED: "smallDeprecated",
  SMALLPLUS_DEPRECATED: "smallPlusDeprecated"
};

export const SEARCH_TYPE = {
  NORMAL: "normal",
  MINIMAL: "minimal"
};

function SearchInputBox(props, ref) {
  const {
    placeholder,
    iconPosition = SEARCH_ICON_POSITIONS.LEFT,
    value,
    type = SEARCH_TYPE.NORMAL,
    disabled,
    onChange,
    onChangeDebounced,
    debounceTime = 300,
    onFocus,
    onBlur,
    onEnter,
    onClear,
    autoFocus = false,
    className,
    size = SEARCH_SIZES.BASE,
    ...restProps
  } = props;
  const [currentValue, setCurrentValue] = useState(value);
  const inputRef = useRef();
  // eslint-disable-next-line kf/hook-func-name,react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce((e) => onChangeDebounced(e), debounceTime),
    [onChangeDebounced]
  );

  useEffect(
    function setValueEffect() {
      setCurrentValue(value);
    },
    [value]
  );

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
      isFocused: () => {
        return inputRef.current?.state?.focused;
      }
    };
  });

  return (
    <React.Fragment>
      <AntInput
        ref={inputRef}
        autoFocus={autoFocus}
        value={currentValue}
        disabled={disabled}
        placeholder={placeholder}
        prefix={
          iconPosition === SEARCH_ICON_POSITIONS.LEFT ? (
            <KfIcon
              icon={KF_ICONS.SEARCH}
              size={ICON_SIZE.SIZE_16}
              stroke={ICON_STROKE.THIN}
              color="--kf-color-gray-500"
            />
          ) : (
            false
          )
        }
        className={`${className} ${styles.search} ${styles[type]} ${
          type === SEARCH_TYPE.NORMAL && styles.normalFontSize
        } ${styles[`${size}`]}`}
        suffix={
          currentValue || iconPosition === SEARCH_ICON_POSITIONS.RIGHT ? (
            <React.Fragment>
              {currentValue && (
                <KfIcon
                  icon={KF_ICONS.CLOSE_ROUND}
                  size={ICON_SIZE.SIZE_16}
                  stroke={ICON_STROKE.BOLD}
                  color="--kf-color-gray-400"
                  className={styles.suffixIcon}
                  onClick={() => {
                    setCurrentValue("");
                    onClear && onClear();
                  }}
                />
              )}
              {iconPosition === SEARCH_ICON_POSITIONS.RIGHT && (
                <KfIcon
                  icon={KF_ICONS.SEARCH}
                  size={ICON_SIZE.SIZE_16}
                  stroke={ICON_STROKE.THIN}
                  color="--kf-color-gray-500"
                />
              )}
            </React.Fragment>
          ) : (
            false
          )
        }
        onChange={(e) => {
          onChange && onChange(e);
          onChangeDebounced && debouncedOnChange(e.target.value);
          setCurrentValue(e.target.value);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        onPressEnter={onEnter}
        data-component="search"
        {...restProps}
      />
    </React.Fragment>
  );
}

export const SEARCH_ICON_POSITIONS = {
  LEFT: "left",
  RIGHT: "right"
};

SearchInputBox.propTypes = {
  iconPosition: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  type: PropTypes.string,
  debounceTime: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeDebounced: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onClear: PropTypes.func,
  onEnter: PropTypes.func,
  className: PropTypes.string,
  autoFocus: PropTypes.bool,
  size: PropTypes.string
};

/**
 * SearchInput is a component that renders a search input box with various functionalities.
 */

export const SearchInput = forwardRef(SearchInputBox);

SearchInput.propTypes = {
  /**
   * Size of the search input.
   */
  size: PropTypes.string,
  /**
   * Icon position for the search input box. Can be either "left" or "right".
   */
  iconPosition: PropTypes.oneOf([
    "SEARCH_ICON_POSITIONS.LEFT",
    "SEARCH_ICON_POSITIONS.RIGHT"
  ]),
  /**
   * Placeholder text for the search input box.
   */
  placeholder: PropTypes.string,
  /**
   * Current value of the search input box.
   */
  value: PropTypes.string,
  /**
   * Type of the input box. E.g., "text", "password", etc.
   */
  type: PropTypes.string,
  /**
   * Debounce time (in milliseconds) for the onChangeDebounced event.
   */
  debounceTime: PropTypes.number,
  /**
   * Determines if the search input box is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Event handler for the onChange event.
   */
  onChange: PropTypes.func,
  /**
   * Event handler for the onChange event with debounced callback.
   */
  onChangeDebounced: PropTypes.func,
  /**
   * Event handler for the onFocus event.
   */
  onFocus: PropTypes.func,
  /**
   * Event handler for the onBlur event.
   */
  onBlur: PropTypes.func,
  /**
   * Event handler for the onClear event.
   */
  onClear: PropTypes.func,
  /**
   * Event handler for the onEnter event.
   */
  onEnter: PropTypes.func,
  /**
   * Additional class name for the search input box container.
   */
  className: PropTypes.string,
  /**
   * Determines if the search input box should be automatically focused on mount.
   */
  autoFocus: PropTypes.bool
};

SearchInput.defaultProps = {
  iconPosition: SEARCH_ICON_POSITIONS.LEFT,
  type: SEARCH_TYPE.NORMAL,
  debounceTime: 300,
  autoFocus: false,
  size: SEARCH_SIZES.BASE
};
